import type { ItemFragmentFragment } from '@apps/www/src/__generated__/graphql';
import { type UserListItemFragmentFragment } from '@apps/www/src/__generated__/graphql';
import SVFollowButtonContainer from '@apps/www/src/www/containers/SVFollowButtonContainer';
import useUIState, { UIStateKeys, useSetUIState } from '@apps/www/src/www/hooks/useUIState';
import SVBottomGridUpsell from '@pkgs/shared-client/components/SVBottomGridUpsell';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import ViewportKind from '@pkgs/shared/enums/ViewportKind';
import { useRouter } from 'next/router';
import { memo } from 'react';
import SVA from './SVA';
import SVAvatar from './SVAvatar';
import { SVButtonSIZES } from './SVButton';
import SVLazyImage from './SVLazyImage';
import SVLink from './SVLink';
import SVLoadingIndicator from './SVLoadingIndicator';
import SVPageMargins from './SVPageMargins';
import SVProBadge from './SVProBadge';
import SVVirtualList from './SVVirtualList';

const HEIGHT = 176;
const HEIGHT_SMALL = 136;

const _ImagesList = memo(
	({
		username,
		items,
	}: {
		username: UserListItemFragmentFragment['username'];
		items: ItemFragmentFragment[];
	}) => {
		const router = useRouter();
		const setDetailGridItems = useSetUIState(UIStateKeys.DETAIL_GRID_ITEMS);

		const handleItemClick = useEventCallback(() => {
			setDetailGridItems(items);
		});

		return (
			<div className="images flex flex-1 space-x-2 overflow-hidden" title={username}>
				{/* TODO: save button on image */}
				{items.map((item) => (
					<SVLink
						className="flex-shrink-0"
						key={item._id}
						to={{
							pathname: router.pathname,
							query: { ...router.query, itemShortID: item.shortID },
						}}
						as={item.url}
						onClick={handleItemClick}
					>
						<SVLazyImage
							className="-sm:h-[88px] h-[176px] w-auto"
							src={item.asset.image.thumbnail}
						/>
					</SVLink>
				))}
			</div>
		);
	},
);

const _Item = memo(
	({ user, isSmallAny }: { user: UserListItemFragmentFragment; isSmallAny: boolean }) => {
		const info =
			user.role || user.company ? (
				<>
					{user.role && <span className="truncate">{user.role}</span>}
					{user.role && user.company && <>&nbsp;</>}
					{user.company && (
						<span className="max-w-full truncate">
							&nbsp;
							<SVA Component={SVLink} to={user.company.url} className="text-gray-400">
								{user.company.name}
							</SVA>
						</span>
					)}
				</>
			) : null;

		return (
			<div className="-sm:mb-10 -sm:flex-col -sm:space-x-0 -sm:space-y-4 mb-16 flex flex-col">
				<div className="-sm:max-w-full -sm:flex-row  -sm:space-y-0 -sm:space-x-4 -sm:py-0 mb-4 flex items-center justify-between py-2 sm:w-full">
					<div className="-sm:min-w-0 -sm:flex-row -sm:space-y-0 flex items-center space-y-1 self-stretch">
						<SVA
							className="-sm:flex-row -sm:space-y-0 flex w-full items-center space-x-4"
							Component={SVLink}
							to={user.url}
						>
							<SVAvatar
								className="h-[36px] w-[36px] flex-shrink-0"
								src={user.avatarURL}
								lazy={true}
							/>
							<span className="flex min-w-0 max-w-full flex-col items-stretch">
								<span className="flex max-w-full items-center font-semibold">
									<span className="truncate">{user.name}</span>
									{user.isPro && (
										<SVProBadge className="duration-over flex-shrink-0 transition-opacity ease-out group-hover:opacity-70" />
									)}
								</span>
								{info && (
									<div className="type-label mb-1 flex max-w-full text-gray-400">
										<span className="max-w-full truncate">{info}</span>
									</div>
								)}
							</span>
						</SVA>
					</div>
					<div className="flex-shrink-0">
						<SVFollowButtonContainer
							userID={user._id}
							canFollow={user.canFollow}
							isFollowing={user.isFollowing}
							isFollowingBack={user.isFollowingBack}
							size={isSmallAny ? SVButtonSIZES.TINY : SVButtonSIZES.SMALL}
						/>
					</div>
				</div>
				<_ImagesList username={user.username} items={user.items.items} />
			</div>
		);
	},
);

type Props = {
	isLoading: boolean;
	users: UserListItemFragmentFragment[] | null;
	onPaginate: (() => void) | null;
	isLimited?: boolean;
};

const SVUsersList = ({ isLoading, users, onPaginate, isLimited }: Props) => {
	const [viewportKind] = useUIState(UIStateKeys.VIEWPORT_KIND);
	const isSmallAny = viewportKind === ViewportKind.MOBILE;

	const childrenHeight = isSmallAny ? 56 + HEIGHT_SMALL + 40 : HEIGHT + 64;

	let emptyMessage: React.ReactNode = null;

	if (!isLoading && users && !users.length) {
		// TODO: Create empty messages component
		emptyMessage = (
			<div className="absolute left-0 right-0 text-center">
				<p>No users here.</p>
			</div>
		);

		emptyMessage = (
			<SVPageMargins className="text-center">
				<br />
				<br />
				{emptyMessage}
			</SVPageMargins>
		);
	}

	const showBottomUpsell = isLimited && !isLoading;

	return (
		<div>
			{emptyMessage}
			<SVPageMargins className="-sm:mr-[var(--page-margin)]">
				<SVVirtualList fixedHeight={childrenHeight} onPaginate={onPaginate}>
					{users &&
						users.map((user) => (
							<_Item key={user._id} user={user} isSmallAny={isSmallAny} />
						))}
				</SVVirtualList>
			</SVPageMargins>

			{isLoading && (
				<SVPageMargins>
					<SVLoadingIndicator />
				</SVPageMargins>
			)}

			{showBottomUpsell && <SVBottomGridUpsell />}
		</div>
	);
};

// TODO: onlyUpdateForProps

export default SVUsersList;
